import { CARD_API_ENDPOINT } from '@/config/consts'
import { useCallback, useState } from 'react'
import { createSmartFetch, createSmartGet } from './useAuthenticatedMutation'

interface WaitlistEntry {
  walletAddress: string
  tenantID: string
  position: number
  referralCode: string
  referredBy?: string
  referralCount: number
  boostScore: number
  status: string
  joinedAt: string
  promotedAt?: string
}

interface WaitlistPositionResponse {
  data: {
    position: number
    entry: WaitlistEntry
  }
}

interface JoinWaitlistResponse {
  data: WaitlistEntry
}

const getWaitlistPosition = createSmartGet(`${CARD_API_ENDPOINT}/v1/vendor/cards/waitlist/position`)

const postJoinWaitlist = createSmartFetch(`${CARD_API_ENDPOINT}/v1/vendor/cards/waitlist/join`, {
  method: 'POST',
})

const postApplyReferral = createSmartFetch(`${CARD_API_ENDPOINT}/v1/vendor/cards/waitlist/referral`, {
  method: 'POST',
})

export function useCardWaitlist() {
  const [entry, setEntry] = useState<WaitlistEntry | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const isOnWaitlist = entry !== null

  const position = entry?.position ?? null
  const referralCode = entry?.referralCode
  const referralCount = entry?.referralCount ?? 0
  const boostScore = entry?.boostScore ?? 0

  const refresh = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response: WaitlistPositionResponse = await getWaitlistPosition()
      setEntry(response.data.entry)
    } catch (err) {
      const isNotFound =
        err instanceof Error && (err.message.includes('404') || err.message.includes('record does not exist'))
      if (isNotFound) {
        setEntry(null)
      } else {
        setError(err instanceof Error ? err : new Error('Failed to load waitlist position'))
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  const join = useCallback(async (referralCodeInput?: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const body = referralCodeInput ? { referralCode: referralCodeInput } : {}
      const response: JoinWaitlistResponse = await postJoinWaitlist(body)
      setEntry(response.data)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to join waitlist'))
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  const applyReferral = useCallback(
    async (code: string) => {
      setIsLoading(true)
      setError(null)
      try {
        await postApplyReferral({ referralCode: code })
        await refresh()
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to apply referral code'))
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [refresh],
  )

  return {
    position,
    referralCode,
    referralCount,
    boostScore,
    isOnWaitlist,
    entry,
    isLoading,
    error,
    join,
    refresh,
    applyReferral,
  }
}
